import { supabase } from "../supabase/client";
import { isAbortError } from "../utils/abort";

export interface OnboardingStatus {
  hasCompletedOnboarding: boolean;
  hasConfiguredPlaid: boolean;
  hasAddedAccount: boolean;
  currentStep: "welcome" | "plaid-setup" | "add-account" | "completed";
}

class OnboardingService {
  /**
   * Check if user has completed onboarding
   */
  async getOnboardingStatus(): Promise<OnboardingStatus> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return this.getDefaultStatus();
      }

      // Plaid is configured at the system level, so we can assume it's always configured
      const hasConfiguredPlaid = true;

      // Check if user has added any accounts (you'll need to implement this based on your accounts table)
      const hasAddedAccount = await this.checkUserHasAccounts(user.id);

      // Check if onboarding was previously completed (stored in user_profiles metadata)
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profileError && profileError.code !== "PGRST116") {
        // PGRST116 = no rows found, which is fine
        console.warn("Error fetching user profile:", profileError);
      }

      const hasCompletedOnboarding = profile?.has_completed_onboarding || false;

      // Debug logging
      console.log("Onboarding status check:", {
        hasCompletedOnboarding,
        profile,
        hasConfiguredPlaid,
        hasAddedAccount,
      });

      // Determine current step
      let currentStep: OnboardingStatus["currentStep"] = "welcome";
      if (!hasConfiguredPlaid) {
        currentStep = "plaid-setup";
      } else if (!hasAddedAccount) {
        currentStep = "add-account";
      } else {
        currentStep = "completed";
      }

      // If user has everything set up, mark onboarding as completed
      if (hasConfiguredPlaid && hasAddedAccount && !hasCompletedOnboarding) {
        await this.markOnboardingComplete();
      }

      return {
        hasCompletedOnboarding:
          hasCompletedOnboarding || (hasConfiguredPlaid && hasAddedAccount),
        hasConfiguredPlaid,
        hasAddedAccount,
        currentStep,
      };
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] Onboarding status aborted", error);
      } else {
        console.error("Error getting onboarding status:", error);
      }
      return this.getDefaultStatus();
    }
  }

  /**
   * Check if user has any linked accounts
   */
  private async checkUserHasAccounts(userId: string): Promise<boolean> {
    try {
      // Check localStorage for Plaid tokens (temporary solution)
      // In production, this should check your accounts table in Supabase
      const plaidTokens = localStorage.getItem("plaid-tokens");
      if (plaidTokens) {
        const tokens = JSON.parse(plaidTokens);
        return tokens && tokens.length > 0;
      }

      // TODO: Check Supabase accounts table when implemented
      // const { data, error } = await supabase
      //   .from('accounts')
      //   .select('id')
      //   .eq('user_id', userId)
      //   .limit(1);
      // return data && data.length > 0;

      return false;
    } catch (error) {
      console.error("Error checking user accounts:", error);
      return false;
    }
  }

  /**
   * Mark onboarding as complete
   */
  async markOnboardingComplete(): Promise<void> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      // Upsert user profile to ensure record exists and mark onboarding as complete
      const { error } = await supabase.from("user_profiles").upsert(
        {
          id: user.id,
          email: user.email || "",
          has_completed_onboarding: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "id",
        }
      );

      if (error) {
        console.error("Error updating user profile:", error);
        throw error;
      }
    } catch (error) {
      console.error("Error marking onboarding complete:", error);
    }
  }

  /**
   * Skip onboarding (user can access it later from settings)
   */
  async skipOnboarding(): Promise<void> {
    await this.markOnboardingComplete();
  }

  /**
   * Reset onboarding (for testing or re-running setup)
   */
  async resetOnboarding(): Promise<void> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      // Upsert to ensure record exists, then set onboarding to false
      await supabase.from("user_profiles").upsert(
        {
          id: user.id,
          email: user.email || "",
          has_completed_onboarding: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "id",
        }
      );
    } catch (error) {
      console.error("Error resetting onboarding:", error);
    }
  }

  /**
   * Get default onboarding status
   */
  private getDefaultStatus(): OnboardingStatus {
    return {
      hasCompletedOnboarding: false,
      hasConfiguredPlaid: false,
      hasAddedAccount: false,
      currentStep: "welcome",
    };
  }
}

export const onboardingService = new OnboardingService();
